import React, { useEffect, useMemo, useState } from 'react';
import {
  WxbAlert,
  WxbButton,
  WxbCard,
  WxbEmpty,
  WxbModal,
  WxbSelect,
  WxbTag,
} from '../wxb-ui';
import TemporalExpressionEditor, { createElapsedExpression } from './TemporalExpressionEditor';
import {
  deriveTransitionPreviews,
  formatCyclePolicySummary,
  formatTemporalExpression,
  formatTimeDrivenTransitionRefresh,
  formatTimeDrivenTransitionStart,
} from './equipmentStateCatalogModel';
import type {
  EquipmentStateAction,
  EquipmentStateAttribute,
  EquipmentStateClassModel,
  EquipmentStateTimeDrivenTransition,
  EquipmentStateValue,
} from '../../types/prodEquipmentStateCatalog';

interface TimeDrivenTransitionModalProps {
  open: boolean;
  attribute: EquipmentStateAttribute | null;
  actions: EquipmentStateAction[];
  initialTransition: EquipmentStateTimeDrivenTransition | null;
  occupiedSourceValueCodes: string[];
  readOnly: boolean;
  onCancel: () => void;
  onSave: (transition: EquipmentStateTimeDrivenTransition) => void;
}

const preferredValueCode = (values: EquipmentStateValue[], matcher: RegExp, fallback: string): string => (
  values.find((value) => matcher.test(value.code.toUpperCase()))?.code ?? fallback
);

const createDefaultTimeDrivenTransition = (
  values: EquipmentStateValue[],
): EquipmentStateTimeDrivenTransition => {
  const initialValue = values.find((value) => value.isInitial)?.code ?? values[0]?.code ?? '';
  const sourceValueCode = preferredValueCode(values, /(^|_)CLEAN($|_)/, values[0]?.code ?? '');
  const targetValueCode = preferredValueCode(values, /CLEAN.*EXPIRED|EXPIRED|DIRTY/, initialValue);
  return {
    sourceValueCode,
    targetValueCode: targetValueCode === sourceValueCode ? initialValue : targetValueCode,
    startRule: { kind: 'STATE_ENTRY' },
    refreshRule: { kind: 'RESET_ON_START_EVENT' },
    expiryRule: {
      kind: 'TEMPORAL_EXPRESSION',
      expression: { ...createElapsedExpression(), value: 72, unit: 'HOUR' },
    },
  };
};

const cloneTransition = (transition: EquipmentStateTimeDrivenTransition): EquipmentStateTimeDrivenTransition => (
  JSON.parse(JSON.stringify(transition)) as EquipmentStateTimeDrivenTransition
);

const TimeDrivenTransitionModal: React.FC<TimeDrivenTransitionModalProps> = ({
  open,
  attribute,
  actions,
  initialTransition,
  occupiedSourceValueCodes,
  readOnly,
  onCancel,
  onSave,
}) => {
  const [draft, setDraft] = useState<EquipmentStateTimeDrivenTransition>(() => createDefaultTimeDrivenTransition([]));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !attribute) return;
    setDraft(initialTransition
      ? cloneTransition(initialTransition)
      : createDefaultTimeDrivenTransition(attribute.values));
    setError(null);
  }, [attribute, initialTransition, open]);

  if (!attribute) return null;
  const valueOptions = attribute.values.map((value) => ({
    value: value.code,
    label: `${value.name} (${value.code})`,
  }));
  const actionOptions = actions.map((action) => ({
    value: action.actionCode,
    label: `${action.name} · ${action.effectTiming === 'START' ? '开始时' : '结束时'}投影`,
  }));
  const submit = () => {
    const sourceValueCode = draft.sourceValueCode.trim().toUpperCase();
    const targetValueCode = draft.targetValueCode.trim().toUpperCase();
    const valueCodes = new Set(attribute.values.map((value) => value.code.trim().toUpperCase()));
    if (!sourceValueCode || !targetValueCode || sourceValueCode === targetValueCode
      || !valueCodes.has(sourceValueCode) || !valueCodes.has(targetValueCode)) {
      setError('请选择两个不同的已声明状态：来源状态和到期后状态。');
      return;
    }
    if (occupiedSourceValueCodes.includes(sourceValueCode)) {
      setError('同一来源状态只能维护一条自动到期边；请编辑已有边或选择另一个来源状态。');
      return;
    }
    if (draft.startRule.kind === 'ACTION_BOUNDARY' && !draft.startRule.actionCode.trim()) {
      setError('请选择启动计时的排产动作。');
      return;
    }
    if (draft.refreshRule.kind === 'RESET_ON_ACTION_BOUNDARY' && !draft.refreshRule.actionCode.trim()) {
      setError('请选择用于刷新计时的排产动作。');
      return;
    }
    if (!Number.isFinite(draft.expiryRule.expression.value) || draft.expiryRule.expression.value <= 0) {
      setError('过期时长必须大于 0。');
      return;
    }
    onSave({
      ...draft,
      sourceValueCode,
      targetValueCode,
      startRule: draft.startRule.kind === 'ACTION_BOUNDARY'
        ? { kind: 'ACTION_BOUNDARY', actionCode: draft.startRule.actionCode.trim().toUpperCase() }
        : { kind: 'STATE_ENTRY' },
      refreshRule: draft.refreshRule.kind === 'RESET_ON_ACTION_BOUNDARY'
        ? { kind: 'RESET_ON_ACTION_BOUNDARY', actionCode: draft.refreshRule.actionCode.trim().toUpperCase() }
        : draft.refreshRule,
    });
  };

  return (
    <WxbModal
      open={open}
      title={initialTransition ? '编辑自动到期边' : '添加自动到期边'}
      okText={initialTransition ? '保存时间边' : '添加时间边'}
      width={820}
      onCancel={onCancel}
      onOk={submit}
      footer={readOnly ? (
        <div className="esc-drawer-actions">
          <WxbButton type="button" variant="ghost" onClick={onCancel}>关闭</WxbButton>
        </div>
      ) : undefined}
    >
      <div className="esc-modal-stack">
        {error && <WxbAlert variant="error" title="时间边尚不能保存">{error}</WxbAlert>}
        <WxbAlert title="未来状态自动投影">
          该定义不需要人工确认：APS 会在所选计划动作的开始或结束时投影状态；当计时到期时，再把同一属性写入目标状态。
        </WxbAlert>
        <div className="esc-form-grid">
          <WxbSelect
            label="来源状态（开始计时）"
            value={draft.sourceValueCode || undefined}
            disabled={readOnly}
            options={valueOptions}
            placeholder="选择进入后需要保持的状态"
            onChange={(value) => setDraft((current) => ({ ...current, sourceValueCode: String(value) }))}
          />
          <WxbSelect
            label="到期后状态"
            value={draft.targetValueCode || undefined}
            disabled={readOnly}
            options={valueOptions}
            placeholder="选择自动写入的状态"
            onChange={(value) => setDraft((current) => ({ ...current, targetValueCode: String(value) }))}
          />
        </div>
        <section className="esc-subsection">
          <div className="esc-subsection-header">
            <div>
              <h3>1. 记时开始方式</h3>
              <p>通常以进入来源状态为准；若需锁定到某个计划操作边界，可明确选择该操作。</p>
            </div>
          </div>
          <WxbSelect
            label="开始方式"
            value={draft.startRule.kind}
            disabled={readOnly}
            options={[
              { value: 'STATE_ENTRY', label: '进入来源状态时（推荐）' },
              { value: 'ACTION_BOUNDARY', label: '指定排产动作投影时' },
            ]}
            onChange={(kind) => setDraft((current) => ({
              ...current,
              startRule: kind === 'ACTION_BOUNDARY'
                ? { kind: 'ACTION_BOUNDARY', actionCode: actions[0]?.actionCode ?? '' }
                : { kind: 'STATE_ENTRY' },
            }))}
          />
          {draft.startRule.kind === 'ACTION_BOUNDARY' && (
            <WxbSelect
              label="开始动作"
              value={draft.startRule.actionCode || undefined}
              disabled={readOnly}
              options={actionOptions}
              placeholder="选择已关联操作库的动作"
              onChange={(actionCode) => setDraft((current) => ({
                ...current,
                startRule: { kind: 'ACTION_BOUNDARY', actionCode: String(actionCode) },
              }))}
            />
          )}
        </section>
        <section className="esc-subsection">
          <div className="esc-subsection-header">
            <div>
              <h3>2. 状态刷新机制</h3>
              <p>决定再次清洗、灭菌或其他动作发生后，是否重置本次有效期。</p>
            </div>
          </div>
          <WxbSelect
            label="刷新方式"
            value={draft.refreshRule.kind}
            disabled={readOnly}
            options={[
              { value: 'RESET_ON_START_EVENT', label: '再次进入来源状态时重置（推荐）' },
              { value: 'RESET_ON_ACTION_BOUNDARY', label: '指定排产动作投影时重置' },
              { value: 'ONE_SHOT', label: '只在首次开始时计时' },
            ]}
            onChange={(kind) => setDraft((current) => ({
              ...current,
              refreshRule: kind === 'RESET_ON_ACTION_BOUNDARY'
                ? { kind: 'RESET_ON_ACTION_BOUNDARY', actionCode: actions[0]?.actionCode ?? '' }
                : { kind: kind as 'RESET_ON_START_EVENT' | 'ONE_SHOT' },
            }))}
          />
          {draft.refreshRule.kind === 'RESET_ON_ACTION_BOUNDARY' && (
            <WxbSelect
              label="刷新动作"
              value={draft.refreshRule.actionCode || undefined}
              disabled={readOnly}
              options={actionOptions}
              placeholder="选择已关联操作库的动作"
              onChange={(actionCode) => setDraft((current) => ({
                ...current,
                refreshRule: { kind: 'RESET_ON_ACTION_BOUNDARY', actionCode: String(actionCode) },
              }))}
            />
          )}
        </section>
        <section className="esc-subsection">
          <div className="esc-subsection-header">
            <div>
              <h3>3. 过期计算方式</h3>
              <p>连续时长与日历期间语义不同：72 小时、90 连续天和 3 个自然月不是同一概念。</p>
            </div>
          </div>
          <TemporalExpressionEditor
            value={draft.expiryRule.expression}
            disabled={readOnly}
            labelPrefix="到期"
            hideCalendarContext
            onChange={(expression) => setDraft((current) => ({
              ...current,
              expiryRule: { kind: 'TEMPORAL_EXPRESSION', expression },
            }))}
          />
        </section>
      </div>
    </WxbModal>
  );
};

interface Props {
  model: EquipmentStateClassModel;
  readOnly: boolean;
  onChange: (model: EquipmentStateClassModel) => void;
  onAddAttribute: () => void;
  onEditAttribute: (index: number) => void;
  onEditAction: (index: number) => void;
  onAddCyclePolicy: () => void;
  onEditCyclePolicy: (index: number) => void;
}

const HYGIENE_VALUE_ORDER = ['CLEAN_EXPIRED', 'CLEAN', 'ASEPTIC', 'DIRTY', 'RINSED'];

const hygieneScore = (attribute: EquipmentStateAttribute): number => {
  if (attribute.kind !== 'DISCRETE') return -1;
  const text = `${attribute.code} ${attribute.name}`.toUpperCase();
  const valueText = attribute.values.map((value) => value.code.toUpperCase()).join(' ');
  return (/(CLEAN|CIP|SIP|RIP|ASEPTIC|洁净|清洗|灭菌)/.test(text) ? 10 : 0)
    + (/(CLEAN|ASEPTIC|DIRTY|RINSED|EXPIRED)/.test(valueText) ? 5 : 0);
};

const orderHygieneValues = (values: EquipmentStateValue[]): EquipmentStateValue[] => (
  [...values].sort((left, right) => {
    const leftIndex = HYGIENE_VALUE_ORDER.indexOf(left.code.toUpperCase());
    const rightIndex = HYGIENE_VALUE_ORDER.indexOf(right.code.toUpperCase());
    return (leftIndex < 0 ? HYGIENE_VALUE_ORDER.length : leftIndex)
      - (rightIndex < 0 ? HYGIENE_VALUE_ORDER.length : rightIndex);
  })
);

const stateLabel = (attribute: EquipmentStateAttribute, valueCode: string): string => {
  const value = attribute.values.find((item) => item.code === valueCode);
  return value ? `${value.name} (${value.code})` : valueCode;
};

const HygienePathEditor: React.FC<Props> = ({
  model,
  readOnly,
  onChange,
  onAddAttribute,
  onEditAttribute,
  onEditAction,
  onAddCyclePolicy,
  onEditCyclePolicy,
}) => {
  const discreteAttributes = useMemo(() => model.attributes.filter((attribute) => attribute.kind === 'DISCRETE'), [model.attributes]);
  const defaultAttributeCode = useMemo(() => (
    [...discreteAttributes].sort((left, right) => hygieneScore(right) - hygieneScore(left))[0]?.code ?? ''
  ), [discreteAttributes]);
  const [selectedAttributeCode, setSelectedAttributeCode] = useState(defaultAttributeCode);
  const [timeModalOpen, setTimeModalOpen] = useState(false);
  const [editingTimeIndex, setEditingTimeIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!discreteAttributes.some((attribute) => attribute.code === selectedAttributeCode)) {
      setSelectedAttributeCode(defaultAttributeCode);
    }
  }, [defaultAttributeCode, discreteAttributes, selectedAttributeCode]);

  const attribute = discreteAttributes.find((item) => item.code === selectedAttributeCode) ?? null;
  const attributeIndex = attribute ? model.attributes.indexOf(attribute) : -1;
  const actionTransitions = useMemo(() => deriveTransitionPreviews(model).filter((transition) => (
    transition.attributeCode === attribute?.code
  )), [attribute?.code, model]);
  const timeDrivenTransitions = attribute?.timeDrivenTransitions ?? [];
  const updateTransitions = (transitions: EquipmentStateTimeDrivenTransition[]) => {
    if (!attribute || attributeIndex < 0) return;
    onChange({
      ...model,
      attributes: model.attributes.map((item, index) => index === attributeIndex
        ? { ...item, timeDrivenTransitions: transitions }
        : item),
    });
  };
  const policyRows = model.cyclePolicies;

  if (!attribute) {
    return (
      <div className="esc-editor-panel">
        <div className="esc-editor-panel-head">
          <div>
            <h3>清洗灭菌路径</h3>
            <p>先在同一类别模型中创建一条离散状态属性，再在这里把动作、到期边和独立周期组合成一条可读路径。</p>
          </div>
        </div>
        <WxbEmpty
          description="尚未定义可用于清洗灭菌路径的离散状态。"
          action={!readOnly ? <WxbButton type="button" onClick={onAddAttribute}>添加状态定义</WxbButton> : undefined}
        />
      </div>
    );
  }

  return (
    <div className="esc-editor-panel esc-hygiene-editor">
      <div className="esc-editor-panel-head esc-hygiene-head">
        <div>
          <h3>清洗灭菌路径</h3>
          <p>状态动作在计划边界直接改变未来状态；自动到期边只负责把状态在未来继续投影。两条轨道分别维护状态有效期和独立维护周期。</p>
        </div>
        <div className="esc-hygiene-head-actions">
          <WxbSelect
            label="状态属性"
            value={attribute.code}
            options={discreteAttributes.map((item) => ({ value: item.code, label: `${item.name} (${item.code})` }))}
            onChange={(value) => setSelectedAttributeCode(String(value))}
          />
          <WxbButton type="button" size="sm" variant="secondary" onClick={() => onEditAttribute(attributeIndex)}>
            维护状态值
          </WxbButton>
        </div>
      </div>

      <WxbAlert title="计划态优先">
        CIP、RIP、SIP、生产等动作均从操作库取用，并在其计划开始或结束时直接写入此状态。实际执行回填只用于校正计划，不作为状态转换的前置条件。
      </WxbAlert>

      <section className="esc-hygiene-section">
        <div className="esc-subsection-header">
          <div>
            <h3>状态路径</h3>
            <p>每张卡片是一种状态；下方的动作行显示由操作库动作投影的状态转移。同一目标状态的多个动作是候选路径，不自动宣称它们已经过验证等效。</p>
          </div>
          <WxbButton type="button" size="sm" variant="ghost" onClick={() => onEditAction(-1)} disabled={readOnly}>
            添加动作
          </WxbButton>
        </div>
        <div className="esc-hygiene-state-rail" aria-label={`${attribute.name} 状态路径`}>
          {orderHygieneValues(attribute.values).map((value) => (
            <WxbCard className="esc-hygiene-state-card" key={value.code}>
              <div className="esc-hygiene-state-title">
                <strong>{value.name}</strong>
                {value.isInitial && <WxbTag color="amber">初始</WxbTag>}
              </div>
              <code>{value.code}</code>
            </WxbCard>
          ))}
        </div>
        <div className="esc-hygiene-action-list">
          {actionTransitions.length === 0 ? (
            <WxbEmpty
              description="尚未有操作库动作写入这条状态属性。"
              action={!readOnly ? <WxbButton type="button" size="sm" onClick={() => onEditAction(-1)}>添加动作</WxbButton> : undefined}
            />
          ) : actionTransitions.map((transition) => {
            const actionIndex = model.actions.findIndex((action) => action.actionCode === transition.actionCode);
            return (
              <WxbCard className="esc-hygiene-action-card" key={transition.key}>
                <div className="esc-hygiene-action-flow">
                  <WxbTag color="blue">{stateLabel(attribute, transition.from)}</WxbTag>
                  <span className="esc-hygiene-arrow" aria-hidden="true">→</span>
                  <div className="esc-hygiene-action-name">
                    <strong>{transition.actionName}</strong>
                    <span>{transition.effectTiming === 'START' ? '计划开始时' : '计划结束时'}投影</span>
                  </div>
                  <span className="esc-hygiene-arrow" aria-hidden="true">→</span>
                  <WxbTag color="green">{stateLabel(attribute, transition.to)}</WxbTag>
                </div>
                {!readOnly && actionIndex >= 0 && (
                  <WxbButton type="button" size="sm" variant="ghost" onClick={() => onEditAction(actionIndex)}>编辑动作</WxbButton>
                )}
              </WxbCard>
            );
          })}
        </div>
      </section>

      <section className="esc-hygiene-section">
        <div className="esc-subsection-header">
          <div>
            <h3>状态有效期</h3>
            <p>例如 CIP 或 RIP 将设备写为 CLEAN，CHT 72 小时到期后自动改为 CLEAN_EXPIRED。这里是状态的一部分，不是独立周期策略。</p>
          </div>
          <WxbButton
            type="button"
            size="sm"
            variant="secondary"
            disabled={readOnly || attribute.values.length < 2}
            onClick={() => {
              setEditingTimeIndex(null);
              setTimeModalOpen(true);
            }}
          >
            添加自动到期边
          </WxbButton>
        </div>
        {timeDrivenTransitions.length === 0 ? (
          <WxbEmpty
            description="尚未定义状态有效期。设备会保持在动作写入的状态，直到下一条动作改变它。"
          />
        ) : (
          <div className="esc-hygiene-time-list">
            {timeDrivenTransitions.map((transition, index) => (
              <WxbCard className="esc-hygiene-time-card" key={`${transition.sourceValueCode}-${index}`}>
                <div className="esc-hygiene-time-main">
                  <WxbTag color="green">{stateLabel(attribute, transition.sourceValueCode)}</WxbTag>
                  <div className="esc-hygiene-time-span">
                    <strong>{formatTemporalExpression(transition.expiryRule.expression)}</strong>
                    <span>{formatTimeDrivenTransitionStart(transition)}；{formatTimeDrivenTransitionRefresh(transition)}</span>
                  </div>
                  <span className="esc-hygiene-arrow" aria-hidden="true">→</span>
                  <WxbTag color="amber">{stateLabel(attribute, transition.targetValueCode)}</WxbTag>
                </div>
                {!readOnly && (
                  <div className="esc-hygiene-time-actions">
                    <WxbButton type="button" size="sm" variant="ghost" onClick={() => {
                      setEditingTimeIndex(index);
                      setTimeModalOpen(true);
                    }}>编辑</WxbButton>
                    <WxbButton type="button" size="sm" variant="ghost" onClick={() => (
                      updateTransitions(timeDrivenTransitions.filter((_, itemIndex) => itemIndex !== index))
                    )}>移除</WxbButton>
                  </div>
                )}
              </WxbCard>
            ))}
          </div>
        )}
      </section>

      <section className="esc-hygiene-section">
        <div className="esc-subsection-header">
          <div>
            <h3>独立清洗 / 维护周期</h3>
            <p>例如每 3 个月或每 90 天的周期 CIP。它产生“应执行”的排产约束，不会直接改写 CLEAN、ASEPTIC 等洁净状态；若同一时间窗口可满足状态和周期，派生层可合并为一次操作。</p>
          </div>
          <WxbButton type="button" size="sm" variant="secondary" disabled={readOnly} onClick={onAddCyclePolicy}>
            添加周期策略
          </WxbButton>
        </div>
        {policyRows.length === 0 ? (
          <WxbEmpty description="尚未定义独立清洗或维护周期。" />
        ) : (
          <div className="esc-hygiene-policy-list">
            {policyRows.map((policy, index) => (
              <WxbCard className="esc-hygiene-policy-card" key={policy.code}>
                <div>
                  <strong>{policy.name}</strong>
                  <code>{policy.code}</code>
                  <p>{formatCyclePolicySummary(policy)}</p>
                </div>
                {!readOnly && <WxbButton type="button" size="sm" variant="ghost" onClick={() => onEditCyclePolicy(index)}>编辑策略</WxbButton>}
              </WxbCard>
            ))}
          </div>
        )}
      </section>

      <TimeDrivenTransitionModal
        open={timeModalOpen}
        attribute={attribute}
        actions={model.actions}
        initialTransition={editingTimeIndex == null ? null : timeDrivenTransitions[editingTimeIndex]}
        occupiedSourceValueCodes={timeDrivenTransitions
          .filter((_, index) => index !== editingTimeIndex)
          .map((transition) => transition.sourceValueCode.trim().toUpperCase())}
        readOnly={readOnly}
        onCancel={() => setTimeModalOpen(false)}
        onSave={(transition) => {
          const next = editingTimeIndex == null
            ? [...timeDrivenTransitions, transition]
            : timeDrivenTransitions.map((item, index) => index === editingTimeIndex ? transition : item);
          updateTransitions(next);
          setTimeModalOpen(false);
        }}
      />
    </div>
  );
};

export default HygienePathEditor;
